/**
 * Browser-safe event helpers. HMAC token minting lives in `event/tokens`
 * so client components can import this file without pulling in Node's
 * `crypto`.
 */
import { EventType, type State } from "@prisma/client";
import {
  EVENT_ZOOM_OWNER_EMAIL,
  ZOOM_AGENDA_MAX_LENGTH,
  ZOOM_REGISTRATION_CLAIM_PREFIX,
  ZOOM_REGISTRATION_CLAIM_TTL_MS,
  ZOOM_SESSION_CLAIM_TTL_MS,
  ZOOM_SESSION_PENDING_PREFIX,
} from "@marble/core/modules/event/constants";
import type { EventHostZoomFields } from "@marble/core/modules/event/types";
import { MARBLE_STATES } from "@marble/core/modules/states/constants";

export const eventDurationMinutes = (start: Date, end: Date): number =>
  Math.max(1, Math.round((end.getTime() - start.getTime()) / 60_000));

/**
 * Parent workshops never share recordings. PD workshops share by default;
 * pass `requested` to opt a PD workshop out.
 */
export const recordingViewableOutsideAdminsForEventType = (
  type: EventType,
  requested?: boolean,
): boolean => {
  if (type === EventType.PARENT_WORKSHOP) {
    return false;
  }
  return requested ?? true;
};

export const eventStartsInTheFuture = (
  start: Date,
  now = new Date(),
): boolean => start.getTime() > now.getTime();

/** Deduped Marble operating states, in canonical order. */
export const uniqueMarbleCeStates = (states: State[]): State[] =>
  MARBLE_STATES.filter((state) => states.includes(state));

/** "NY, NJ" for CE copy. Empty when the workshop does not offer CE. */
export const workshopCeStatesLabel = (states: State[]): string =>
  uniqueMarbleCeStates(states).join(", ");

/** Localized sentence for emails, calendar files, and share copy. */
export const workshopCeApplicableCopy = (
  states: State[],
  language: "ENGLISH" | "SPANISH" = "ENGLISH",
): string | null => {
  const label = workshopCeStatesLabel(states);
  if (!label) {
    return null;
  }
  return language === "SPANISH"
    ? `El crédito CE es aplicable en ${label}.`
    : `CE credit is applicable in ${label}.`;
};

/** Timestamped claim written to `Event.zoomId` while Zoom create is in flight. */
export const newZoomSessionPendingClaim = (nowMs = Date.now()): string =>
  `${ZOOM_SESSION_PENDING_PREFIX}:${nowMs}`;

export const isZoomSessionPendingClaim = (
  zoomId: string | null | undefined,
): zoomId is string =>
  !!zoomId &&
  (zoomId === ZOOM_SESSION_PENDING_PREFIX ||
    zoomId.startsWith(`${ZOOM_SESSION_PENDING_PREFIX}:`));

/** A real Zoom webinar/meeting id, not a create-in-flight claim. */
export const isLiveZoomSessionId = (
  zoomId: string | null | undefined,
): zoomId is string => !!zoomId && !isZoomSessionPendingClaim(zoomId);

/**
 * True when a pending claim can be stolen: older than the TTL, or a
 * legacy bare `__pending__` with no timestamp (those never expire on
 * their own and would block retries forever after a crash).
 */
export const isExpiredZoomSessionClaim = (
  zoomId: string,
  nowMs = Date.now(),
): boolean => {
  if (!isZoomSessionPendingClaim(zoomId)) {
    return false;
  }
  const prefix = `${ZOOM_SESSION_PENDING_PREFIX}:`;
  if (!zoomId.startsWith(prefix)) {
    return true;
  }
  const claimedAt = Number(zoomId.slice(prefix.length));
  if (!Number.isFinite(claimedAt)) {
    return true;
  }
  return nowMs - claimedAt >= ZOOM_SESSION_CLAIM_TTL_MS;
};

/** Timestamped claim written to `Participant.zoomRegistrantId` while a Zoom POST is in flight. */
export const newZoomRegistrationClaim = (nowMs = Date.now()): string =>
  `${ZOOM_REGISTRATION_CLAIM_PREFIX}:${nowMs}`;

export const isZoomRegistrationClaim = (
  zoomRegistrantId: string | null | undefined,
): zoomRegistrantId is string =>
  !!zoomRegistrantId &&
  (zoomRegistrantId === ZOOM_REGISTRATION_CLAIM_PREFIX ||
    zoomRegistrantId.startsWith(`${ZOOM_REGISTRATION_CLAIM_PREFIX}:`));

/**
 * True when a registrant claim can be stolen: older than the TTL, or a
 * legacy bare `CLAIMING` with no timestamp (those never expire on their
 * own and would block retries forever after a crash).
 */
export const isExpiredZoomRegistrationClaim = (
  zoomRegistrantId: string,
  nowMs = Date.now(),
): boolean => {
  if (!isZoomRegistrationClaim(zoomRegistrantId)) {
    return false;
  }
  const prefix = `${ZOOM_REGISTRATION_CLAIM_PREFIX}:`;
  if (!zoomRegistrantId.startsWith(prefix)) {
    return true;
  }
  const claimedAt = Number(zoomRegistrantId.slice(prefix.length));
  if (!Number.isFinite(claimedAt)) {
    return true;
  }
  return nowMs - claimedAt >= ZOOM_REGISTRATION_CLAIM_TTL_MS;
};

export const zoomAgendaFromDescription = (
  description: string | null | undefined,
): string | undefined => {
  if (!description) {
    return undefined;
  }
  return description.length > ZOOM_AGENDA_MAX_LENGTH
    ? description.slice(0, ZOOM_AGENDA_MAX_LENGTH)
    : description;
};

export const uniqueEventHostIds = (hostIds?: number[]): number[] => [
  ...new Set(hostIds ?? []),
];

/**
 * Public hosts stay listed; a host in both lists is public. Backup hosts
 * are Zoom alternative hosts only.
 */
export const eventHostAssignments = (
  hostIds?: number[],
  backupHostIds?: number[],
): { hostId: number; isBackup: boolean }[] => {
  const publicIds = uniqueEventHostIds(hostIds);
  const publicIdSet = new Set(publicIds);
  const backupIds = uniqueEventHostIds(backupHostIds).filter(
    (hostId) => !publicIdSet.has(hostId),
  );
  return [
    ...publicIds.map((hostId) => ({ hostId, isBackup: false })),
    ...backupIds.map((hostId) => ({ hostId, isBackup: true })),
  ];
};

/**
 * Zoom `userId` path param for the Host that owns the webinar/meeting:
 * their Zoom user id, or licensed email if they have no stored zoomId.
 */
export const zoomUserIdForHost = (host: {
  id: number;
  zoomId?: string | null;
  email?: string | null;
}): string => {
  const zoomId = host.zoomId?.trim();
  if (zoomId) {
    return zoomId;
  }
  const email = host.email?.trim();
  if (email) {
    return email;
  }
  throw new Error(
    `Host ${host.id} needs a zoomId or email to own the Zoom session`,
  );
};

const _isEventZoomOwnerHost = (
  host: EventHostZoomFields,
  hostZoomUserId: string,
): boolean => {
  const hostRef = hostZoomUserId.toLowerCase();
  if (host.zoomId && host.zoomId === hostZoomUserId) {
    return true;
  }
  return (host.email?.trim().toLowerCase() ?? "") === hostRef;
};

/**
 * Host emails to send as Zoom alternative hosts, excluding the Zoom
 * account that owns the session (matched by zoom id or email). Event
 * sessions are always owned by `hello@marblehealth.com`.
 */
export const alternativeHostEmailsForZoom = ({
  hosts,
  hostZoomUserId = EVENT_ZOOM_OWNER_EMAIL,
}: {
  hosts: EventHostZoomFields[];
  hostZoomUserId?: string;
}): string[] => {
  const emails = new Set<string>();
  for (const host of hosts) {
    const email = host.email?.trim();
    if (!email || _isEventZoomOwnerHost(host, hostZoomUserId)) {
      continue;
    }
    emails.add(email);
  }
  return [...emails];
};

/**
 * Webinar panelists for the Hosts passed in. Include the Zoom owner host
 * when the caller wants a unique panelist join_url to store on
 * Event.zoomOwnerJoinUrl; workshop Hosts' URLs go on EventHost.
 * `excludeZoomOwner` skips hello@ — Zoom accepts that add and never lists
 * it back, so a daily retry burns the per-panelist add limit.
 */
export const webinarPanelistsForZoom = ({
  hosts,
  excludeZoomOwner = false,
}: {
  hosts: EventHostZoomFields[];
  excludeZoomOwner?: boolean;
}): { name: string; email: string }[] => {
  const panelists: { name: string; email: string }[] = [];
  const seen = new Set<string>();
  for (const host of hosts) {
    const email = host.email?.trim();
    if (!email) {
      continue;
    }
    if (
      excludeZoomOwner &&
      _isEventZoomOwnerHost(host, EVENT_ZOOM_OWNER_EMAIL)
    ) {
      continue;
    }
    const key = email.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    panelists.push({ name: host.name?.trim() || email, email });
  }
  return panelists;
};

export const isWorkshopEventType = (type: EventType): boolean =>
  type === EventType.PARENT_WORKSHOP ||
  type === EventType.PROFESSIONAL_DEVELOPMENT_WORKSHOP;

export const eventRecordingPagePath = (eventId: number): string =>
  `/events/${eventId}/recording`;
